import axios, { AxiosResponse } from 'axios';
import { GameV2Configuration, SiteExperienceConfiguration } from '../../common-types/ScreensConfigurations.type';
import { CommonDataConfig } from '../../common-types/CommonDataConfig.type';
import { Ticket } from '../../common-types/Ticket.type';
import { Site } from '../../common-types/Site.type';
import { GameInfo } from '../../common-types/GameInfo.type';
import { Campaign } from '../../common-types/Campaign.type';

interface AxiosFetchTicketDataResponse {
  config: {
    screens: GameV2Configuration,
  },
  ticket: Ticket,
  campaign: Campaign,
  err?: string,
}

export interface AxiosFetchSiteDataResponse {
  site: Site | null | undefined,
  err?: string,
  config: {
    screens: SiteExperienceConfiguration,
    commons: CommonDataConfig,
  }
}

export interface FetchTicketDataResponse {
  config: {
    screens: GameV2Configuration,
  },
  ticket: Ticket,
  campaign: Campaign,
}

interface AxiosFetchGameResultResponse {
  code: string,
  play: {
    win: boolean,
    disp: string,
  },
  err?: string,
}
interface FetchGameResultResponse {
  code: string,
  play: {
    win: boolean,
    disp: string,
  },
}
interface FetchGameListResponse {
  games: GameInfo[],
  err?: string,
}
interface SendEmailResponse {
  ok: boolean;
  err?: string;
}

interface SendEmailResponse {
  ok: boolean;
  err?: string;
}

interface PreviewResponse {
  gameConfig: {
    screens: GameV2Configuration,
  },
  siteConfig: {
    screens: SiteExperienceConfiguration,
    commons: CommonDataConfig,
  }
  ticket: Ticket,
  campaign: Campaign,
  site: Site,
}

export class ApiService {
  private readonly host: string;

  public constructor () {
    this.host = process.env.REACT_APP_API_BASE_URL as string;
  }

  public fetchTicketData (ticketCode: string, siteKey: string, customerUid: string): Promise<FetchTicketDataResponse> {
    const url = `${this.host}/v3/gameConfig/${ticketCode}?siteKey=${siteKey}&customerUid=${customerUid}`;

    return axios.get(url).then((response: AxiosResponse<AxiosFetchTicketDataResponse>) => {
      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }
      return response.data;
    });
  }

  public fetchSiteData (siteKey: string): Promise<AxiosFetchSiteDataResponse> {
    const url = `${this.host}/v3/siteConfig/${siteKey}`;

    return axios.get(url).then((response: AxiosResponse<AxiosFetchSiteDataResponse>) => {
      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }
      return response.data;
    });
  }

  public fetchGameResult (ticketCode: string, shopperUid: string, siteKey: string, cartUid: string): Promise<FetchGameResultResponse> {
    const url = `${this.host}/v3/playTicket/${ticketCode}`;
    const params = { siteKey, shopperUid, cartUid };
    return axios.get(url, { params }).then((response: AxiosResponse<AxiosFetchGameResultResponse>) => {
      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }
      return response.data;
    });
  }

  public async fetchGamesList (cartId: string, customerId: string, authKey: string): Promise<FetchGameListResponse> {
    const url = `${this.host}/cart/games`;
    // eslint-disable-next-line camelcase
    const params = { cartId, customerId, auth_key: authKey };

    const waitingPull = 500;

    for (let retryNumber = 20; retryNumber > 0; retryNumber -= 1) {
      // eslint-disable-next-line no-await-in-loop
      const response: AxiosResponse<FetchGameListResponse> = await axios.get(url, { params });

      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }

      const { games } = response.data;

      if (Array.isArray(games) && games.length > 0) {
        return response.data;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), waitingPull);
      });
    }
    return {
      games: [],
    };
  }

  public sendOptin (ticketCode: string, optinName: string, optinValue: boolean): Promise<string> {
    const url = `${this.host}/ticket/${ticketCode}/optins.${optinName}/${optinValue}`;
    return axios.get(url).then((response: AxiosResponse<string>) => {
      return response.data;
    });
  }

  public sendEmail (ticketCode: string, email: string): Promise<SendEmailResponse> {
    const url = `${this.host}/ticket/${ticketCode}/email?email=${encodeURIComponent(email)}`;
    return axios.get(url).then((response: AxiosResponse<SendEmailResponse>) => {
      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }
      return response.data;
    });
  }

  public fetchPreviewData (campaignId: string): Promise<PreviewResponse> {
    const url = `${this.host}/v3/previewConfig/${campaignId}`;
    return axios.get(url).then((response: AxiosResponse<PreviewResponse>) => {
      return response.data;
    });
  }
}

export const apiServiceInstance = new ApiService();
